/* eslint-disable max-len */
import React, {
  useCallback, useContext, useEffect, useRef, useState
} from 'react';
import PropTypes from 'prop-types';
import { extend, useDataModel, useLazyDataModel } from '@thd-nucleus/data-sources';
import { ExperienceContext, useConfigService } from '@thd-nucleus/experience-context';
import { Sticky } from '@thd-olt-component-react/sticky';
import { ProductResultsContext } from './ProductResultsContext';
import { FilterAndSortPlaceholder } from './FilterAndSort/FilterAndSortPlaceholder';
import { PrimaryFilters } from './PrimaryFilters/PrimaryFilters';
import { publish } from '../publisher';
import { DrawerAndAppliedRefinements as Drawer } from './FilterAndSort/DrawerAndAppliedRefinements';
import { MainFilter } from './FilterAndSort/MainFilter';
import {
  getAppliedDimensions,
  getClearAllWithSort,
  getRefinementUrlWithSort,
  sortAscending,
  sortDimensions,
  sortRefinementKeys,
  sortSelectedDimensions,
  findCustomPriceRange,
  getDimensionsForUrl,
  getRefinemntsLabelsForUrl,
  filtersCatStyleCheck,
  sortOrder,
  onMultiStoreSelectionChange,
  createDimensionWithRefinements,
  storeLifecycleEvent,
  extractNavParamFromClearAllURL,
  addRefinementIfNotExists,
  isPlpFulfillmentLabel,
  transformFulfillmentDimension
} from '../product-results-helpers';
import {
  BROWSE_ALL_REFINEMENT,
  FULFILLMENT_UI_LABELS,
  IN_STORE_CHIP_DIMENSION_LABEL,
  GET_IT_FAST_DIMENSION_LABEL,
  ON_DISPLAY_ITEM
} from '../utils/constants';
import {
  findMatchingRefinementInDimension,
  resetDimensionLabel,
  isPlpDimensionLabel
} from '../utils/fulfillment-helpers';
import '../styles/filter-and-sort.scss';
import OnDisplayContext from './OnDisplayContext';
import { productResultsDataModel } from '../models/productResultsDataModel';
import { productRefinementCountDataModel } from '../models/refinementCountDataModel';
import { getFirstNonEmptyObject } from '../utils/searchHelpers';
import { useDeliveryOptionsContext } from '../context/DeliveryOptionsContext';

const {
  func, bool, number, string, shape, arrayOf
} = PropTypes;

const ResultsFilterAndSortDrawer = ({
  onDimensionsChange,
  onRefinementSelected,
  onSortChange,
  stickyOffset,
  multiStoreSelection,
  enableMultiStore,
  onDisplay,
  onDisplayChange,
  disableStickyFilter,
  isTableView,
  hideViewAll,
  isGetItFastOnPlp,
  isPlpSpeedEnabled,
  searchModelData: composableData,
  loading: composableLoading,
  queryVariables: composableQueryVariables,
  pageContext: composablePageContext
}) => {
  const { channel, customContext } = useContext(ExperienceContext);
  const { data: wrapperData, queryVariables: contextQueryVariables, pageContext: contextPageContext, loading: wrapperLoading } = useContext(ProductResultsContext);
  const controlledData = getFirstNonEmptyObject(wrapperData, composableData);
  const queryVariables = contextQueryVariables || composableQueryVariables;
  const pageContext = contextPageContext || composablePageContext;

  // Extract variables needed for refinement count API call
  const {
    storeId = '',
    storefilter = 'ALL',
    isBrandPricingPolicyCompliant = false,
    additionalSearchParams = {},
    loyaltyMembershipInput = {}
  } = queryVariables || {};

  const [onDisplayClick, setOnDisplayClick] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [primaryFilterDrawerOpen, setPrimaryFilterDrawerOpen] = useState(false);
  const clearAllRefinement = useRef();
  const appliedSelectionForDrawer = useRef([]);
  const desktopSort = useRef('');
  const {
    setForceInitialSync
  } = useDeliveryOptionsContext() || {};

  const isMobile = channel === 'mobile';
  const isBrowsePage = pageContext?.pageType === 'browse';
  const variables = { ...customContext?.variables || {} };
  const isControlledData = typeof wrapperData !== 'undefined' || typeof wrapperLoading !== 'undefined' || typeof composableData !== 'undefined';

  const defaultOptions = {
    variables,
    ssr: true,
    skip: isControlledData || (!variables?.keyword && !variables?.navParam)
  };

  const { data: componentData, loading: componentLoading } = useDataModel('searchModel', defaultOptions);
  const viewProductCountEnabled = useConfigService('fs-prop:viewProductCountEnabled');
  const smartFilterEnabled = useConfigService('fs-prop:smartFilterEnabled') && isBrowsePage;
  const data = isControlledData ? controlledData : componentData;
  const controlledDataLoading = typeof composableData !== 'undefined' ? composableLoading : wrapperLoading;
  const loading = isControlledData ? controlledDataLoading : componentLoading;

  const [getRefinementCount, refinementCountResponse] = useLazyDataModel('refinementCountModel');

  const {
    appliedDimensions, taxonomy, dimensions, searchReport, metadata, primaryFilters
  } = data?.searchModel || {};

  const { keyword = null, totalProducts } = searchReport || {};

  const { canonicalUrl = '', clearAllRefinementsURL = '', categoryID: categoryId = '' } = metadata || {};

  const [activeDimension, setActiveDimension] = useState(dimensions?.length > 0 ? dimensions[0] : {});

  const [selectedDimensions, setSelectedDimensions] = useState(
    appliedDimensions ? getAppliedDimensions(appliedDimensions) : []
  );
  const [productCount, setProductCount] = useState(totalProducts);

  const handleSortOrderChange = ({ output: result }) => {
    if (result?.sortby && result?.sortorder) {
      desktopSort.current = `${result.sortby}:${result.sortorder}`;
    }
  };

  useEffect(() => {
    setSelectedDimensions(getAppliedDimensions(appliedDimensions));
    window.LIFE_CYCLE_EVENT_BUS.on('sortorder.value', handleSortOrderChange);
    let _appliedDimensions = getAppliedDimensions(appliedDimensions);
    if (onDisplay === 'true') {
      setSelectedDimensions([..._appliedDimensions, ON_DISPLAY_ITEM]);
    } else {
      setSelectedDimensions(_appliedDimensions);
    }
    if (viewProductCountEnabled) {
      setProductCount(totalProducts);
    }
  }, [appliedDimensions, totalProducts, viewProductCountEnabled, onDisplay]);

  useEffect(() => {
    if (viewProductCountEnabled && refinementCountResponse.error) {
      // eslint-disable-next-line no-console
      console.error('Error retrieving refinement count', refinementCountResponse.error);
    }
    const refinementProductCount = refinementCountResponse?.data?.refinementCountModel?.searchReport?.totalProducts;
    if (viewProductCountEnabled && !refinementCountResponse.error && typeof refinementProductCount === 'number') {
      setProductCount(refinementProductCount);
    }
  }, [refinementCountResponse, viewProductCountEnabled]);

  const isRefinementPresent = useCallback(() => {
    return selectedDimensions.some(
      (dimension) => dimension?.refinements.some((ref) => ref?.label === ON_DISPLAY_ITEM?.refinements[0]?.label)
    );
  }, [ON_DISPLAY_ITEM, selectedDimensions]);

  // adding chips into drawer
  const addRefinement = useCallback(() => {
    if (!isRefinementPresent()) {
      setSelectedDimensions([...selectedDimensions, { ...ON_DISPLAY_ITEM }]);
    }
  }, [isRefinementPresent, ON_DISPLAY_ITEM, selectedDimensions]);

  // removing chip from drawer
  const removeRefinement = useCallback(() => {
    if (isRefinementPresent() && selectedDimensions.length) {
      setSelectedDimensions(selectedDimensions.filter((item) => {
        return !item?.refinements.some((refinement) => refinement.refinementLabel === 'On Display');
      }));
    }
  }, [isRefinementPresent, selectedDimensions]);

  const keepCurrentSelection = () => {
    return getAppliedDimensions(selectedDimensions);
  };

  useEffect(() => {
    setOnDisplayClick(isRefinementPresent());
    appliedSelectionForDrawer.current = keepCurrentSelection();
  }, [selectedDimensions]);

  const onDisplayData = {
    onDisplay,
    onDisplayChange,
    isStoreDisplay: data?.searchModel?.metadata?.isStoreDisplay || null,
    itemCount: data?.searchModel?.metadata?.productCount?.inStore
  };

  const extractBaseNavParam = useCallback(() => {
    const match = clearAllRefinementsURL.match(/(N-[^/?]+)/);
    return match ? match[1] : '';
  }, [clearAllRefinementsURL]);

  const ensureNavParamPrefix = useCallback(
    (navParam) => (navParam.startsWith('N-') ? navParam : `N-${navParam}`), []
  );

  const collectRefinementKeys = useCallback(
    (selectedRefinements = []) => selectedRefinements.flatMap(
      (group) => (group?.refinements || [])
        .filter((ref) => ref?.refinementKey)
        .map((ref) => ref.refinementKey)
    ), []
  );

  const extractCustomPriceFilter = useCallback((selectedRefinements = []) => {
    if (!Array.isArray(selectedRefinements)) {
      return null;
    }

    const priceRef = selectedRefinements
      .flatMap((group) => group?.refinements || [])
      .find((ref) => ref?.lowerbound && ref?.upperbound);

    return priceRef
      ? {
        rangefilter: 'price',
        lowerBound: Number(priceRef.lowerbound),
        upperBound: Number(priceRef.upperbound)
      }
      : null;
  }, []);

  const getNavRootForApply = useCallback((baseNavParam) => {
    if (isBrowsePage) {
      return baseNavParam || '';
    }
    if (baseNavParam.includes('Z')) {
      // strip previously appended keys
      return extractBaseNavParam();
    }
    const firstZ = baseNavParam.indexOf('Z');
    const secondZ = baseNavParam.indexOf('Z', firstZ + 1);
    if (firstZ !== -1 && secondZ !== -1) {
      return baseNavParam.slice(0, secondZ);
    }
    return baseNavParam;
  }, [extractBaseNavParam, isBrowsePage]);

  const getNavParamForClearAll = useCallback((baseNavParam) => {
    if (isBrowsePage) {
      return extractBaseNavParam();
    }
    const firstZ = baseNavParam.indexOf('Z');
    if (firstZ === -1) {
      return extractBaseNavParam();
    }
    // remove everything after first refinement marker
    return extractBaseNavParam().substring(0, firstZ);
  }, [extractBaseNavParam, isBrowsePage]);

  const buildNavParam = useCallback((baseNavParam, selectedRefinements) => {
    if (!selectedRefinements.length) {
      return getNavParamForClearAll(baseNavParam);
    }
    const root = getNavRootForApply(baseNavParam);
    const keys = collectRefinementKeys(selectedRefinements);
    return keys.length ? `${root}Z${keys.join('Z')}` : root;
  }, [
    collectRefinementKeys,
    getNavParamForClearAll,
    getNavRootForApply
  ]);

  const executeGetTotalProductsCount = useCallback((updatedSelectedRefinements = []) => {
    if (viewProductCountEnabled) {
      try {
        const baseNavParam = extractNavParamFromClearAllURL(data) || '';
        const navParamRaw = buildNavParam(baseNavParam, updatedSelectedRefinements);
        const customPriceFilter = extractCustomPriceFilter(updatedSelectedRefinements);
        const productVariables = {
          navParam: ensureNavParamPrefix(navParamRaw),
          keyword,
          storefilter,
          isBrandPricingPolicyCompliant,
          storeId,
          channel: channel.toUpperCase(),
          additionalSearchParams,
          loyaltyMembershipInput,
          filter: customPriceFilter
        };

        getRefinementCount({ variables: productVariables });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('executeGetTotalProductsCount failed', err);
      }
    }
  }, [
    additionalSearchParams,
    buildNavParam,
    channel,
    data,
    extractCustomPriceFilter,
    ensureNavParamPrefix,
    extractNavParamFromClearAllURL,
    getRefinementCount,
    isBrandPricingPolicyCompliant,
    keyword,
    loyaltyMembershipInput,
    storeId,
    storefilter,
    viewProductCountEnabled
  ]);

  // update selected refinements list
  const updateSelectedRefinements = useCallback(({
    refinement, dimension, radio, multiRefinements, removeRefinementChip
  }) => {
    clearAllRefinement.current = undefined;
    let updatedDimensions = selectedDimensions.filter((dim) => dim?.label !== 'Clear');
    const getItFastDimensionApplied = selectedDimensions.find((dim) => dim?.label === GET_IT_FAST_DIMENSION_LABEL);

    let index = selectedDimensions.findIndex((dim) => {
      let labelToCheck;

      if (isPlpSpeedEnabled && getItFastDimensionApplied && dimension?.label === IN_STORE_CHIP_DIMENSION_LABEL) {
        labelToCheck = GET_IT_FAST_DIMENSION_LABEL;
      } else if (isPlpSpeedEnabled && isPlpDimensionLabel(dimension?.label, isPlpSpeedEnabled) && !getItFastDimensionApplied) {
        labelToCheck = resetDimensionLabel(dimension?.label, refinement?.label, isPlpSpeedEnabled);
      } else {
        labelToCheck = dimension?.label;
      }
      return dim.label.toUpperCase() === labelToCheck.toUpperCase();
    });

    if (multiRefinements) {
      updatedDimensions = [
        ...selectedDimensions,
        {
          label: dimension.label,
          refinements: [...multiRefinements]
        }
      ];
    }

    if (radio && refinement === undefined) {
      updatedDimensions = selectedDimensions.filter((item) => item.label !== 'Review Rating');
    }
    if (refinement?.clear) {
      return [];
    }
    if (refinement && index >= 0) {
      // dimension already exists in selected dimensions
      updatedDimensions = selectedDimensions.slice();
      let selectedRefinement = -1;

      if (isPlpSpeedEnabled && isPlpDimensionLabel(dimension.label, true)) {
        // Radio behavior: only one refinement at a time
        const { refinement: matchedRefinement, dimension: matchedDimension } = findMatchingRefinementInDimension(
          dimension, refinement.refinementKey
        ) || {};

        // Existing fulfillment chip was clicked to be removed
        if (removeRefinementChip) {
          updatedDimensions.splice(index, 1);
          // Reset to Browse All fulfillment option
          updatedDimensions.push({
            label: FULFILLMENT_UI_LABELS.BROWSE_ALL,
            refinements: [BROWSE_ALL_REFINEMENT]
          });
        }

        // Add fulfillment refinement
        if (matchedRefinement && !removeRefinementChip) {
          updatedDimensions = updatedDimensions.filter((dim) => !isPlpDimensionLabel(dim.label, true));
          updatedDimensions.push({
            label: resetDimensionLabel(matchedDimension?.label || dimension.label, matchedRefinement?.label, true),
            refinements: [matchedRefinement]
          });
        }
      } else {
        // Other dimensions: checkbox behavior (can add multiple)
        if (dimension.dimensionId === 'custom_price') {
          let priceRangeList = updatedDimensions[index].refinements.filter((ref) => ref.refinementKey !== refinement.refinementKey);
          updatedDimensions[index].refinements = priceRangeList;
        }

        if (updatedDimensions.length > 0) {
          selectedRefinement = updatedDimensions[index].refinements.findIndex(
            (ref) => ref.refinementKey === refinement.refinementKey);
        }

        if (radio && selectedRefinement === -1) {
          updatedDimensions[index].refinements = [];
        }

        if (selectedRefinement >= 0) {
          // Remove existing refinement (toggle off)
          updatedDimensions[index].refinements = updatedDimensions[index].refinements
            .filter((_refinement) => (_refinement.refinementKey !== refinement.refinementKey) || radio);
          if (updatedDimensions[index].refinements.length === 0) {
            updatedDimensions.splice(index, 1);
          }
        } else {
          // Add new refinement
          updatedDimensions[index].refinements = updatedDimensions[index].refinements.slice();
          addRefinementIfNotExists(updatedDimensions[index].refinements, refinement);
        }
      }
    } else if (refinement && index === -1) {
      // dimension does not exist in selected dimensions
      if (isPlpSpeedEnabled && isPlpDimensionLabel(dimension.label, true)) {
        updatedDimensions = updatedDimensions.filter((dim) => !isPlpDimensionLabel(dim.label, true));
      }

      updatedDimensions.push({
        label: isPlpSpeedEnabled && isPlpDimensionLabel(dimension.label, true)
          ? resetDimensionLabel(dimension.label, refinement.label, isPlpSpeedEnabled) : dimension.label,
        refinements: [refinement]
      });
    }

    return updatedDimensions;
  }, [isPlpSpeedEnabled, selectedDimensions]);

  const selectRefinement = useCallback(({
    refinement, dimension, radio, multiRefinements, isChecked, removeRefinementChip
  }) => {
    if (isChecked !== undefined
      && (refinement?.refinementKey === 'onDisplay' || refinement?.label === 'Pick Up Today')) {
      if (isChecked) {
        addRefinement();
      } else {
        removeRefinement();
      }
      return;
    }
    const updatedSelectedRefinements = updateSelectedRefinements({
      refinement, dimension, radio, multiRefinements, removeRefinementChip
    });
    setSelectedDimensions(updatedSelectedRefinements);
    executeGetTotalProductsCount(updatedSelectedRefinements);
  }, [
    addRefinement,
    removeRefinement,
    executeGetTotalProductsCount,
    updateSelectedRefinements
  ]);

  if (!dimensions) {
    return <FilterAndSortPlaceholder numOf={5} />;
  }
  if (dimensions.length === 0) {
    return null;
  }

  const { breadCrumbs = [] } = taxonomy || {};

  const handleSecondaryFilterDrawerToggle = (isOpen) => {
    if (isOpen) {
      setSelectedDimensions(appliedSelectionForDrawer.current);
      // If available, force sync between Super toggle and Fulfillment options
      // when opening the Secondary Filters Drawer
      if (isPlpSpeedEnabled) {
        setForceInitialSync(true);
      }
    } else {
      appliedSelectionForDrawer.current = keepCurrentSelection();
    }
    setDrawerOpen(!drawerOpen);
    publish('change-secondary-filter', !drawerOpen);
  };

  const handlePrimaryDrawerToggle = (isOpen, dimension, selectedRefinements, name) => {
    setPrimaryFilterDrawerOpen(isOpen);
    setActiveDimension(dimension);

    if (isOpen) {
      setSelectedDimensions(appliedSelectionForDrawer.current);
    } else {
      appliedSelectionForDrawer.current = keepCurrentSelection();
    }

    // close performed, keep all previous selections
    // apply performed, keep only applied selections, remove other selections
    const prevSelections = appliedSelectionForDrawer.current
      .filter((item) => item.label !== dimension.label);
    const newDims = selectedRefinements?.length
      ? createDimensionWithRefinements(dimension.label, selectedRefinements) : [];

    appliedSelectionForDrawer.current = name === 'close'
      ? [...prevSelections, ...newDims]
      : keepCurrentSelection();
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
  };

  // handle primary filter drawer toggle
  // builds url and call dimension changed
  /* istanbul ignore next */
  const applyFilters = () => {
    document.body.classList.add('sui-opacity-50');
    setTimeout(() => {
      if (clearAllRefinement.current !== undefined) {
        appliedSelectionForDrawer.current = [];
        onDimensionsChange({ refinement: clearAllRefinement.current });
        closeDrawer();
        document.body.classList.remove('sui-opacity-50');
        return;
      }
      let sortedSelectedDimensions = sortSelectedDimensions(selectedDimensions);
      if (selectedDimensions && Object.keys(selectedDimensions).length > 0) {
        const updatedSelectedDimensions = selectedDimensions.map((dim) => {
          if (isPlpSpeedEnabled && isPlpFulfillmentLabel(dim.label)) {
            return transformFulfillmentDimension(dim);
          }
          return dim;
        });
        storeLifecycleEvent('product-results.change-filters-refinements-secondary', updatedSelectedDimensions);
      }
      // get refinement keys and sort them by id
      let refinementKeys = sortedSelectedDimensions
        .flatMap((dim) => dim.refinements)
        .map((ref) => ref.refinementKey);

      let sortedRefinementKeys = sortRefinementKeys(refinementKeys);

      // get dimensions that may go on the url
      let dimensionsIncludedInUrl = getDimensionsForUrl(sortedSelectedDimensions);

      // get refinements from the previous dimension list
      let refinementsIncludedInUrl = dimensionsIncludedInUrl
        ?.flatMap((_dimension) => _dimension.refinements.sort(sortAscending));
      let length = refinementsIncludedInUrl && refinementsIncludedInUrl.length;
      let lastRefinement = refinementsIncludedInUrl && refinementsIncludedInUrl[length - 1];
      let urlParts = clearAllRefinementsURL?.split('?')[0].split('/');

      // get custom price range
      const priceDimension = selectedDimensions.find((_dimension) => _dimension.label === 'Price');
      const [lowerbound, upperbound] = findCustomPriceRange(priceDimension?.refinements);
      const canonicalUrlParts = canonicalUrl.split('?');
      let queryParams = canonicalUrlParts.length > 1 ? canonicalUrlParts[1] : '';
      if (lowerbound || upperbound) {
        let queries = queryParams.split('&')
          .filter((query) => !query.toLowerCase().includes('lowerbound') && !query.toLowerCase().includes('upperbound'));
        queryParams = queries.join('&');
        queryParams += `&lowerbound=${lowerbound}&upperbound=${upperbound}`;
      }

      queryParams = filtersCatStyleCheck(queryParams);

      queryParams = sortOrder({ desktopSort, queryParams });

      const nValue = urlParts.find((part) => part.startsWith('N-')) || '';
      let refinementsIds = nValue;
      if (sortedRefinementKeys.length > 0) {
        refinementsIds += 'Z' + sortedRefinementKeys.join('Z');
      }
      // get refinement labels for url
      const refinementsLablesForUrl = getRefinemntsLabelsForUrl(refinementsIncludedInUrl);
      urlParts.splice(urlParts.indexOf(nValue), 0, ...refinementsLablesForUrl);

      urlParts.splice(urlParts.indexOf(nValue), 1, refinementsIds);
      lastRefinement = { ...lastRefinement, url: urlParts.join('/') + '?' + queryParams };
      // appliedSelectionForDrawer.current = [];
      // pass dimension
      if (lastRefinement?.refinementKey === 'onDisplay') {
        onDimensionsChange({ refinement: lastRefinement, onDisplay: true });
      } else {
        onDimensionsChange({ refinement: lastRefinement });
      }
      handleSecondaryFilterDrawerToggle(false);
      closeDrawer();
      document.body.classList.remove('sui-opacity-50');
    }, 0);
  };

  const onSmartFilterResponse = () => {
    // Preserve fulfillment dimension if it exists in selectedDimensions
    const fulfillmentDim = selectedDimensions.find((dim) => isPlpFulfillmentLabel(dim.label) || dim.label.toUpperCase() === 'GET IT FAST'
    );

    setSelectedDimensions(fulfillmentDim ? [fulfillmentDim] : []);
  };

  const handleSmartFilterSelectedRefinements = (smartDimensions) => {
    clearAllRefinement.current = undefined;
    const { setByFilter = [], setByUser = {}, defaultFilterSelection } = smartDimensions || {};
    const { userRefinement, userDimension } = setByUser || {};

    if (defaultFilterSelection && setByFilter.length > 0) {
      // Find fulfillment dimension in selectedDimensions
      const fulfillmentDim = selectedDimensions.find((dim) => (isPlpFulfillmentLabel(dim.label) || dim.label.toUpperCase() === 'BROWSE ALL' || dim.label.toUpperCase() === 'GET IT FAST'
      ));

      // Check if fulfillment dimension exists in setByFilter
      const hasFulfillmentInFilter = setByFilter.some((dim) => (isPlpFulfillmentLabel(dim.label) || dim.label.toUpperCase() === 'GET IT FAST'
      ));

      // Add fulfillment dimension into setByFilter if not exists
      const updatedDimensions = fulfillmentDim && !hasFulfillmentInFilter ? [...setByFilter, fulfillmentDim] : [...setByFilter];
      setSelectedDimensions(updatedDimensions);
      executeGetTotalProductsCount(updatedDimensions);
    } else if (userRefinement && userDimension) {
      selectRefinement({ refinement: userRefinement, dimension: userDimension });
    }
  };

  const smartFilterProperties = { categoryId, handleSmartFilterSelectedRefinements, selectedDimensions, onSmartFilterResponse };

  const handleClearAll = () => {
    const url = getClearAllWithSort({ metadata, searchReport });
    clearAllRefinement.current = {
      url,
      clear: true
    };
    removeRefinement();
    setOnDisplayClick(false);
    setSelectedDimensions([]);
    executeGetTotalProductsCount({
      updatedSelectedRefinements: [],
      wrapperData,
      keyword,
      storefilter,
      isBrandPricingPolicyCompliant,
      storeId,
      channel,
      additionalSearchParams,
      loyaltyMembershipInput,
      getRefinementCount,
      clearAllRefinementsURL,
      isBrowsePage
    });
  };

  ResultsFilterAndSortDrawer.handlePrimaryDrawerToggle = handlePrimaryDrawerToggle;
  const sortedDimensions = sortDimensions({
    dimensions, searchReport, metadata, showSmartFilter: smartFilterEnabled, isPlpSpeedEnabled
  });

  const onRefinementChange = ({ refinement, dimension }) => {
    setPrimaryFilterDrawerOpen(false);
    document.body.classList.add('sui-opacity-50');
    /* istanbul ignore next */
    setTimeout(() => {
      if (onDimensionsChange && dimension) {
        const updatedSelectedRefinements = updateSelectedRefinements({ refinement, dimension });
        setSelectedDimensions(updatedSelectedRefinements);
        const refinementUrl = getRefinementUrlWithSort({ dimension, metadata, refinement, searchReport });
        const { label: dimensionName } = dimension;
        const updatedRefinement = {
          ...refinement,
          url: refinementUrl,
          dimensionName
        };
        onDimensionsChange({ refinement: updatedRefinement });
      } else if (onDimensionsChange && !dimension) {
        onDimensionsChange({ refinement });
      }
      document.body.classList.remove('sui-opacity-50');
    }, 0);
  };

  return (
    <OnDisplayContext.Provider value={{ onDisplayClick, setOnDisplayClick }}>
      <div className="sui-overflow-x-auto" data-component="ResultsFilterAndSortDrawer">
        <Drawer
          appliedDimensions={selectedDimensions}
          breadCrumbs={breadCrumbs}
          isOpen={drawerOpen}
          onSortByChange={onSortChange}
          onRefinementChange={onRefinementChange}
          onRefinementSelected={onRefinementSelected || selectRefinement}
          metadata={metadata}
          normalizedDimensions={sortedDimensions}
          searchReport={searchReport}
          toggleDrawer={handleSecondaryFilterDrawerToggle}
          onDisplayData={onDisplayData}
          loading={loading}
          enableMultiStore={enableMultiStore}
          onMultiStoreSelectionChange={onMultiStoreSelectionChange}
          multiStoreSelection={multiStoreSelection}
          onDimensionsChange={applyFilters}
          clearAll={handleClearAll}
          isTableView={isTableView}
          productCount={productCount}
          smartFilterProperties={smartFilterProperties}
          isPlpSpeedEnabled={isPlpSpeedEnabled}
        />
        {!disableStickyFilter ? (
          <div className="sui-h-14">
            <Sticky
              animation="slide"
              className="sui-w-full sui-z-100"
              offsetMargin={stickyOffset}
              position="top"
              visibleByDefault
            >
              <PrimaryFilters
                activeDimension={activeDimension}
                appliedDimensions={selectedDimensions}
                dimensions={dimensions}
                primaryFilters={primaryFilters}
                isOpen={primaryFilterDrawerOpen}
                onRefinementChange={onRefinementChange}
                onCloseDrawer={handlePrimaryDrawerToggle}
                onSecondaryFilterDrawerToggle={handleSecondaryFilterDrawerToggle}
                canonicalUrl={canonicalUrl}
                baseUrl={clearAllRefinementsURL}
                hideViewAll={hideViewAll}
                desktopSort={desktopSort}
              />
            </Sticky>
          </div>
        ) : (
          <PrimaryFilters
            activeDimension={activeDimension}
            appliedDimensions={selectedDimensions}
            dimensions={dimensions}
            primaryFilters={primaryFilters}
            isOpen={primaryFilterDrawerOpen}
            onCloseDrawer={handlePrimaryDrawerToggle}
            onRefinementChange={onRefinementChange}
            onSecondaryFilterDrawerToggle={handleSecondaryFilterDrawerToggle}
            canonicalUrl={canonicalUrl}
            baseUrl={clearAllRefinementsURL}
            hideViewAll={hideViewAll}
            desktopSort={desktopSort}
          />
        )}
        {isMobile && !isGetItFastOnPlp && (
          <MainFilter
            dimensions={sortedDimensions}
            enableMultiStore={enableMultiStore}
            multiStoreSelection={multiStoreSelection}
            onChange={onDisplay === 'true' ? onDisplayChange : onRefinementChange}
            onDisplayData={onDisplayData}
            onMultiStoreSelectionChange={onMultiStoreSelectionChange}
            isPrimaryFilters
          />
        )}
      </div>
    </OnDisplayContext.Provider>
  );
};

ResultsFilterAndSortDrawer.propTypes = {
  onDimensionsChange: func.isRequired,
  onRefinementSelected: func,
  onSortChange: func.isRequired,
  stickyOffset: number,
  enableMultiStore: bool,
  multiStoreSelection: string,
  onDisplay: string,
  onDisplayChange: func,
  disableStickyFilter: bool,
  isTableView: bool,
  hideViewAll: bool,
  isGetItFastOnPlp: bool,
  isPlpSpeedEnabled: bool,
  loading: bool,
  searchModelData: shape({
    searchModel: shape({
      products: arrayOf(
        shape({
          identifiers: shape({
            productType: string
          })
        })
      ),
      searchReport: shape({
        startIndex: number,
        pageSize: number,
        keyword: string,
        sortBy: string,
        sortOrder: string
      })
    })
  }),
  queryVariables: shape({}),
  pageContext: shape({})
};

ResultsFilterAndSortDrawer.defaultProps = {
  onRefinementSelected: null,
  stickyOffset: 0,
  enableMultiStore: false,
  multiStoreSelection: null,
  onDisplay: 'false',
  onDisplayChange: null,
  disableStickyFilter: false,
  isTableView: false,
  hideViewAll: false,
  isGetItFastOnPlp: false,
  isPlpSpeedEnabled: false,
  loading: false,
  searchModelData: undefined,
  queryVariables: undefined,
  pageContext: undefined
};

ResultsFilterAndSortDrawer.displayName = 'ResultsFilterAndSortDrawer';

ResultsFilterAndSortDrawer.dataModel = extend(
  MainFilter, Drawer, productRefinementCountDataModel, productResultsDataModel
);
export { ResultsFilterAndSortDrawer };
